'use client';

import { useEffect, useMemo, useRef, useState } from 'react';

import { MARKER_SETTLE_DELAY } from './constants';
import { getSectionMeasurements, getViewBox } from './geometry';
import { getSettledMarkerPolicy } from './policy';
import { positionPriceMarkers, selectPriceMarkers } from './selection';
import { normalizeSectionId } from './section-ids';
import { getBestSectionPrices } from './ticket-prices';
import type {
  ArenaPriceMarker,
  MarkerDensity,
  Rectangle,
  UseArenaPriceMarkersOptions,
  ViewBox,
} from './types';

export function useArenaPriceMarkers({
  mapRef,
  containerRef,
  ticketGroups,
  selectedSections,
  focusedSection,
  mapIdentity,
  enabled,
}: UseArenaPriceMarkersOptions): ArenaPriceMarker[] {
  const sectionPrices = useMemo(
    () => getBestSectionPrices(ticketGroups, selectedSections),
    [selectedSections, ticketGroups]
  );
  const [markers, setMarkers] = useState<ArenaPriceMarker[]>([]);
  const markersRef = useRef<ArenaPriceMarker[]>([]);
  const stableMarkerSelectionRef = useRef<ArenaPriceMarker[]>([]);
  const markerDensityRef = useRef<MarkerDensity | undefined>();
  const forceSelectionRef = useRef(true);
  const sectionPricesRef = useRef(sectionPrices);
  const selectedSectionsRef = useRef(selectedSections);
  const focusedSectionRef = useRef(focusedSection);
  const hoveredSectionRef = useRef<string | undefined>();
  const scheduleMeasurementRef = useRef<(() => void) | null>(null);
  const initialViewBoxRef = useRef<ViewBox | null>(null);

  useEffect(() => {
    sectionPricesRef.current = sectionPrices;
    stableMarkerSelectionRef.current = [];
    markerDensityRef.current = undefined;
    forceSelectionRef.current = true;
    scheduleMeasurementRef.current?.();
  }, [sectionPrices]);

  useEffect(() => {
    selectedSectionsRef.current = selectedSections;
    focusedSectionRef.current = focusedSection;
    stableMarkerSelectionRef.current = [];
    markerDensityRef.current = undefined;
    forceSelectionRef.current = true;
    scheduleMeasurementRef.current?.();
  }, [focusedSection, selectedSections]);

  useEffect(() => {
    if (!enabled) {
      markersRef.current = [];
      stableMarkerSelectionRef.current = [];
      markerDensityRef.current = undefined;
      forceSelectionRef.current = true;
      setMarkers([]);
      return;
    }

    const mapRoot = mapRef.current?.getMapRootElement();
    const container = containerRef.current;

    if (!mapRoot || !container) {
      markersRef.current = [];
      stableMarkerSelectionRef.current = [];
      markerDensityRef.current = undefined;
      forceSelectionRef.current = true;
      setMarkers([]);
      return;
    }

    initialViewBoxRef.current = null;

    const rectangleCache = new Map<Element, Rectangle>();
    let containerBounds = '';
    let frame: number | null = null;
    let hoverExitTimer: number | null = null;
    let disposed = false;
    const updateDisplayedMarkers = (next: ArenaPriceMarker[]) => {
      if (areMarkersEqual(markersRef.current, next)) return;
      markersRef.current = next;
      setMarkers(next);
    };

    const measure = () => {
      frame = null;
      if (disposed) return;

      if (!sectionPricesRef.current.length) {
        stableMarkerSelectionRef.current = [];
        forceSelectionRef.current = false;
        updateDisplayedMarkers([]);
        return;
      }

      const svg = mapRoot.querySelector('svg');
      const viewBox = svg instanceof SVGSVGElement ? getViewBox(svg) : null;

      if (viewBox && !initialViewBoxRef.current) {
        initialViewBoxRef.current = viewBox;
      }

      const initialViewBox = initialViewBoxRef.current;
      const zoomScale =
        viewBox && initialViewBox
          ? Math.max(
              initialViewBox.width / viewBox.width,
              initialViewBox.height / viewBox.height
            )
          : 1;
      const containerRect = container.getBoundingClientRect();
      const nextBounds = `${containerRect.left}:${containerRect.top}:${containerRect.width}:${containerRect.height}`;
      if (containerBounds !== nextBounds) {
        rectangleCache.clear();
        containerBounds = nextBounds;
      }
      const measuredSections = getSectionMeasurements({
        mapRoot,
        container,
        sections: sectionPricesRef.current,
        rectangleCache,
      });
      const viewport = {
        width: containerRect.width,
        height: containerRect.height,
      };
      const policy = getSettledMarkerPolicy(
        zoomScale,
        viewport,
        markerDensityRef.current
      );
      const shouldSelectMarkers =
        forceSelectionRef.current ||
        policy.kind !== markerDensityRef.current ||
        !stableMarkerSelectionRef.current.length;

      if (shouldSelectMarkers) {
        const selectedMarkers = selectPriceMarkers({
          sections: measuredSections,
          policy,
          selectedSectionIds: selectedSectionsRef.current,
          focusedSectionId: focusedSectionRef.current,
          viewport,
        });

        stableMarkerSelectionRef.current = selectedMarkers;
        markerDensityRef.current = policy.kind;
        forceSelectionRef.current = false;
      }

      const focusedButton = document.activeElement;
      const activeSection =
        focusedButton instanceof HTMLElement &&
        container.contains(focusedButton) &&
        focusedButton.matches(
          '[data-testid^="arena-price-marker-"]:focus-visible'
        )
          ? focusedButton.getAttribute('data-section-id')
          : hoveredSectionRef.current;
      const activeMarkers = activeSection
        ? selectPriceMarkers({
            sections: measuredSections.filter(
              ({ sectionId }) => sectionId === activeSection
            ),
            policy: { kind: policy.kind, maxMarkers: 1 },
            selectedSectionIds: selectedSectionsRef.current,
            focusedSectionId: focusedSectionRef.current,
            viewport,
          })
        : [];
      const nextMarkers = positionPriceMarkers({
        markers: [
          ...activeMarkers,
          ...stableMarkerSelectionRef.current.filter(
            ({ sectionId }) =>
              !activeMarkers.some((marker) => marker.sectionId === sectionId)
          ),
        ].slice(0, policy.maxMarkers ?? undefined),
        sections: measuredSections,
        viewport,
      });

      updateDisplayedMarkers(nextMarkers);
    };

    const scheduleMeasurement = () => {
      if (disposed || frame !== null) return;
      frame = window.requestAnimationFrame(measure);
    };

    let selectionRefreshTimer: number | null = null;
    const requestSelectionRefresh = () => {
      if (disposed) return;
      if (selectionRefreshTimer !== null) {
        window.clearTimeout(selectionRefreshTimer);
      }

      selectionRefreshTimer = window.setTimeout(() => {
        selectionRefreshTimer = null;
        forceSelectionRef.current = true;
        scheduleMeasurement();
      }, MARKER_SETTLE_DELAY);
    };

    const handleInteraction = (event: Event) => {
      if (
        event.target instanceof Element &&
        event.target.closest('[data-testid^="arena-price-marker-"]')
      ) {
        const svg = mapRoot.querySelector('svg');
        const forwarded =
          event instanceof WheelEvent
            ? new WheelEvent(event.type, event)
            : typeof TouchEvent !== 'undefined' && event instanceof TouchEvent
              ? new TouchEvent(event.type, {
                  bubbles: true,
                  cancelable: event.cancelable,
                  touches: Array.from(event.touches),
                  targetTouches: Array.from(event.targetTouches),
                  changedTouches: Array.from(event.changedTouches),
                })
              : null;
        if (svg && forwarded && !svg.dispatchEvent(forwarded))
          event.preventDefault();
      }
      if (
        event.type === 'pointermove' &&
        event instanceof MouseEvent &&
        event.buttons === 0
      )
        return;
      rectangleCache.clear();
      scheduleMeasurement();
      requestSelectionRefresh();
    };

    const handleMapMutation = (mutations: MutationRecord[]) => {
      rectangleCache.clear();
      scheduleMeasurement();

      if (
        mutations.some(
          ({ type, attributeName }) =>
            type === 'attributes' &&
            (attributeName === 'viewBox' || attributeName === 'transform')
        )
      ) {
        requestSelectionRefresh();
      }
    };

    const cancelHoverExit = () => {
      if (hoverExitTimer !== null) window.clearTimeout(hoverExitTimer);
      hoverExitTimer = null;
    };

    const clearHoveredSection = (event?: Event) => {
      if (
        event?.type === 'pointerdown' &&
        event.target instanceof Element &&
        event.target.closest('[data-testid^="arena-price-marker-"]')
      )
        return;
      cancelHoverExit();
      if (!hoveredSectionRef.current) return;
      hoveredSectionRef.current = undefined;
      scheduleMeasurement();
    };

    const updateHoveredSection = (event: Event) => {
      // Touch generates compatibility mouse events; only real mouse hover counts.
      if (
        !(event instanceof PointerEvent) ||
        event.pointerType !== 'mouse' ||
        event.buttons > 0
      )
        return;

      const sectionElement =
        event.target instanceof Element
          ? event.target.closest('[data-section-id]')
          : null;
      const nextSectionId = normalizeSectionId(
        sectionElement?.getAttribute('data-section-id') ?? ''
      );
      const nextHoveredSection = nextSectionId || undefined;

      if (!nextHoveredSection) {
        if (hoveredSectionRef.current && hoverExitTimer === null) {
          hoverExitTimer = window.setTimeout(() => clearHoveredSection(), 120);
        }
        return;
      }
      cancelHoverExit();

      if (hoveredSectionRef.current === nextHoveredSection) return;

      hoveredSectionRef.current = nextHoveredSection;
      scheduleMeasurement();
    };

    scheduleMeasurementRef.current = scheduleMeasurement;

    const mutationObserver = new MutationObserver(handleMapMutation);
    mutationObserver.observe(mapRoot, {
      attributes: true,
      attributeFilter: ['data-section-id', 'viewBox', 'transform'],
      childList: true,
      subtree: true,
    });

    const resizeObserver =
      typeof ResizeObserver === 'undefined'
        ? null
        : new ResizeObserver(() => {
            rectangleCache.clear();
            scheduleMeasurement();
            requestSelectionRefresh();
          });
    resizeObserver?.observe(container);

    const interactionEvents = [
      'click',
      'pointerdown',
      'pointermove',
      'pointerup',
      'pointercancel',
      'pointerleave',
      'wheel',
      'touchstart',
      'touchmove',
      'touchend',
      'touchcancel',
    ];
    interactionEvents.forEach((eventName) => {
      container.addEventListener(eventName, handleInteraction, {
        capture: true,
        passive: eventName !== 'wheel' && !eventName.startsWith('touch'),
      });
    });

    const hoverEvents = ['pointerover', 'pointermove', 'pointerup'];
    hoverEvents.forEach((eventName) => {
      container.addEventListener(eventName, updateHoveredSection, {
        capture: true,
        passive: true,
      });
    });
    container.addEventListener('mouseleave', clearHoveredSection);
    container.addEventListener('pointerleave', clearHoveredSection);
    container.addEventListener('focusin', scheduleMeasurement);
    container.addEventListener('focusout', scheduleMeasurement);
    const clearHoverEvents = ['pointerdown', 'pointercancel'];
    clearHoverEvents.forEach((eventName) => {
      container.addEventListener(eventName, clearHoveredSection, true);
    });

    scheduleMeasurement();

    return () => {
      disposed = true;
      cancelHoverExit();
      if (frame !== null) window.cancelAnimationFrame(frame);
      if (selectionRefreshTimer !== null) {
        window.clearTimeout(selectionRefreshTimer);
      }
      mutationObserver.disconnect();
      resizeObserver?.disconnect();
      interactionEvents.forEach((eventName) => {
        container.removeEventListener(eventName, handleInteraction, true);
      });
      hoverEvents.forEach((eventName) => {
        container.removeEventListener(eventName, updateHoveredSection, true);
      });
      container.removeEventListener('mouseleave', clearHoveredSection);
      container.removeEventListener('pointerleave', clearHoveredSection);
      container.removeEventListener('focusin', scheduleMeasurement);
      container.removeEventListener('focusout', scheduleMeasurement);
      clearHoverEvents.forEach((eventName) => {
        container.removeEventListener(eventName, clearHoveredSection, true);
      });
      hoveredSectionRef.current = undefined;
      markersRef.current = [];
      stableMarkerSelectionRef.current = [];
      markerDensityRef.current = undefined;
      forceSelectionRef.current = true;
      setMarkers([]);

      if (scheduleMeasurementRef.current === scheduleMeasurement) {
        scheduleMeasurementRef.current = null;
      }
    };
  }, [containerRef, enabled, mapIdentity, mapRef]);

  return markers;
}

function areMarkersEqual(
  current: readonly ArenaPriceMarker[],
  next: readonly ArenaPriceMarker[]
): boolean {
  if (current.length !== next.length) return false;

  return current.every((marker, index) => {
    const nextMarker = next[index];
    return (
      marker.sectionId === nextMarker.sectionId &&
      marker.price === nextMarker.price &&
      marker.availableQuantity === nextMarker.availableQuantity &&
      marker.isFeatured === nextMarker.isFeatured &&
      marker.left === nextMarker.left &&
      marker.top === nextMarker.top &&
      marker.density === nextMarker.density &&
      marker.isSelected === nextMarker.isSelected &&
      marker.isVisible === nextMarker.isVisible
    );
  });
}
